export const invalidDateMessage = 'Enter dates in YYYY-MM format.';
export const reversedDateMessage = 'The start month must not be after the end month.';
export const datePeriods = [
	{ value: 'any-time', label: 'Any time', years: 0 },
	{ value: 'past-year', label: 'Past year', years: 1 },
	{ value: 'past-3-years', label: 'Past 3 years', years: 3 },
	{ value: 'past-5-years', label: 'Past 5 years', years: 5 },
];

export const isValidMonth = (value) => {
	if (typeof value !== 'string' || !/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) {
		return false;
	}

	const year = Number(value.slice(0, 4));
	return year >= 1000 && year <= 9999;
};

export const isValidTimeZone = (timeZone) => {
	if (typeof timeZone !== 'string' || !timeZone) {
		return false;
	}

	try {
		new Intl.DateTimeFormat('en-US', { timeZone }).format();
		return true;
	} catch (error) {
		return false;
	}
};

const getZonedParts = (timestamp, timeZone) => {
	const formatter = new Intl.DateTimeFormat('en-US', {
		timeZone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hourCycle: 'h23',
	});
	const parts = Object.fromEntries(
		formatter.formatToParts(new Date(timestamp))
			.filter((part) => part.type !== 'literal')
			.map((part) => [part.type, Number(part.value)])
	);

	return parts;
};

const zonedDateTimeToTimestamp = (year, month, day, timeZone) => {
	const target = Date.UTC(year, month - 1, day, 0, 0, 0);
	let guess = target;

	for (let attempt = 0; attempt < 3; attempt += 1) {
		const parts = getZonedParts(guess, timeZone);
		const represented = Date.UTC(
			parts.year,
			parts.month - 1,
			parts.day,
			parts.hour,
			parts.minute,
			parts.second
		);
		guess += target - represented;
	}

	return Math.floor(guess / 1000);
};

export const datePeriodToRange = (value, timeZone, now = Date.now()) => {
	const period = datePeriods.find((candidate) => candidate.value === value);
	if (!period) {
		return null;
	}
	if (!period.years) {
		return [undefined, undefined];
	}

	const today = getZonedParts(now, timeZone);
	return [
		zonedDateTimeToTimestamp(
			today.year - period.years,
			today.month,
			today.day,
			timeZone
		),
		undefined,
	];
};

export const rangeToDatePeriod = (range, timeZone, now = Date.now()) => {
	const [start, end] = Array.isArray(range) ? range : [undefined, undefined];
	if (start === undefined && end === undefined) {
		return 'any-time';
	}
	if (end !== undefined) {
		return '';
	}

	const match = datePeriods.find((period) => {
		if (!period.years) {
			return false;
		}
		const periodRange = datePeriodToRange(period.value, timeZone, now);
		return Number(periodRange?.[0]) === Number(start);
	});

	return match?.value || '';
};

export const getDatePeriodLabel = (value) => (
	datePeriods.find((period) => period.value === value)?.label || ''
);

export const monthToBound = (monthValue, timeZone, isEnd) => {
	const [year, month] = monthValue.split('-').map(Number);
	if (!isEnd) {
		return zonedDateTimeToTimestamp(year, month, 1, timeZone);
	}

	const nextMonth = month === 12 ? 1 : month + 1;
	const nextYear = month === 12 ? year + 1 : year;
	return zonedDateTimeToTimestamp(nextYear, nextMonth, 1, timeZone) - 1;
};

export const timestampToMonth = (timestamp, timeZone) => {
	if (!Number.isFinite(Number(timestamp))) {
		return '';
	}

	const formatter = new Intl.DateTimeFormat('en-CA', {
		timeZone,
		year: 'numeric',
		month: '2-digit',
	});
	const parts = Object.fromEntries(
		formatter.formatToParts(new Date(Number(timestamp) * 1000))
			.filter((part) => part.type !== 'literal')
			.map((part) => [part.type, part.value])
	);

	return `${parts.year}-${parts.month}`;
};

export const parseDateRoute = (startMonth, endMonth, timeZone) => {
	const hasInvalidDate = (startMonth && !isValidMonth(startMonth))
		|| (endMonth && !isValidMonth(endMonth));

	if (hasInvalidDate) {
		return { error: invalidDateMessage };
	}
	if (startMonth && endMonth && startMonth > endMonth) {
		return { error: reversedDateMessage };
	}

	return {
		error: '',
		start: startMonth ? monthToBound(startMonth, timeZone, false) : undefined,
		end: endMonth ? monthToBound(endMonth, timeZone, true) : undefined,
	};
};

export const getDateRefinementLabel = (refinement, timeZone, now = Date.now()) => {
	const numericValue = refinement.numericValue ?? refinement.value;
	const operator = refinement.operator
		|| (/^≥/.test(refinement.label) ? '≥' : '')
		|| (/^≤/.test(refinement.label) ? '≤' : '');
	if (['>=', '≥'].includes(operator)) {
		const period = rangeToDatePeriod([numericValue, undefined], timeZone, now);
		const periodLabel = getDatePeriodLabel(period);
		if (periodLabel) {
			return periodLabel;
		}
	}

	const month = timestampToMonth(
		numericValue,
		timeZone
	);
	if (!month) {
		return refinement.label;
	}

	if (['>=', '≥'].includes(operator)) {
		return `From ${month}`;
	}
	if (['<=', '≤'].includes(operator)) {
		return `Through ${month}`;
	}
	return month;
};
